import { DomainError } from "@econ-v1/rpc";
import { StageRegistryStore } from "./stage-registry.js";
export const DEFAULT_COMPOSITION_LIMITS = {
    maxChildren: 32,
    maxDepth: 8,
};
/**
 * Coordinates nested stage loads without widening a child stage's authority.
 * The loader remains responsible for preparation, hash/version checks, module
 * validation, and opening the per-app RPC session.
 */
export class CompositionManager {
    #limits;
    #loader;
    #registry;
    constructor(options) {
        this.#limits = {
            maxChildren: options.limits?.maxChildren ?? DEFAULT_COMPOSITION_LIMITS.maxChildren,
            maxDepth: options.limits?.maxDepth ?? DEFAULT_COMPOSITION_LIMITS.maxDepth,
        };
        if (this.#limits.maxChildren < 1 || this.#limits.maxDepth < 0) {
            throw new RangeError("Composition limits must be non-negative, with at least one child slot");
        }
        this.#loader = options.loader;
        this.#registry = options.registry;
    }
    async load(appName, root, navigate, canCommit) {
        const entry = this.#requireEntry(appName);
        if (entry.ui.kind !== "stage") {
            throw new DomainError({
                code: "invalid_request",
                details: { appName },
                message: "Only top-level stage apps can be mounted as routes",
            });
        }
        const graph = { count: 1 };
        const node = this.#createNode(appName, root, 0, [appName], graph);
        return await this.#loadNode(node, navigate, canCommit);
    }
    async #loadChild(parent, appName, slot, navigate) {
        this.#assertUsable(parent);
        const parentEntry = this.#requireEntry(parent.appName);
        if (!parentEntry.ui.composes.includes(appName)) {
            throw new DomainError({
                code: "unauthorized",
                details: { appName, hostAppName: parent.appName },
                message: `Stage ${parent.appName} is not allowed to compose ${appName}`,
            });
        }
        const childEntry = this.#requireEntry(appName);
        if (childEntry.ui.kind !== "widget") {
            throw new DomainError({
                code: "invalid_request",
                details: { appName },
                message: "Composed children must declare ui.kind 'widget'",
            });
        }
        if (parent.path.includes(appName)) {
            throw new DomainError({
                code: "validation_failed",
                details: { appName, path: [...parent.path] },
                message: `Stage composition cycle detected at ${appName}`,
            });
        }
        const depth = parent.depth + 1;
        if (depth > this.#limits.maxDepth) {
            throw new DomainError({
                code: "conflict",
                details: { maxDepth: this.#limits.maxDepth },
                message: "Stage composition depth limit exceeded",
            });
        }
        parent.graph.count += 1;
        if (parent.graph.count > this.#limits.maxChildren) {
            throw new DomainError({
                code: "conflict",
                details: { maxChildren: this.#limits.maxChildren },
                message: "Stage composition child limit exceeded",
            });
        }
        const child = this.#createNode(appName, slot, depth, [...parent.path, appName], parent.graph);
        try {
            const mounted = await this.#loadNode(child, navigate, () => this.#isUsable(parent));
            parent.children.add(mounted);
            return mounted;
        }
        catch (error) {
            child.active = false;
            throw error;
        }
    }
    async #loadNode(node, navigate, canCommit) {
        const stageLoad = (appName, slot) => this.#loadChild(node, appName, slot, navigate);
        const rawOptions = { stageLoad };
        const mounted = await this.#loader.loadRaw(node.appName, node.root, navigate, () => canCommit() && this.#isUsable(node), rawOptions);
        node.mounting = false;
        return this.#wrapMounted(node, mounted);
    }
    #createNode(appName, root, depth, path, graph) {
        return {
            active: true,
            appName,
            children: new Set(),
            depth,
            graph,
            mounting: true,
            path,
            root,
        };
    }
    #wrapMounted(node, mounted) {
        let unmounted = false;
        return {
            unmount: async () => {
                if (unmounted) {
                    return;
                }
                unmounted = true;
                node.active = false;
                const children = [...node.children].reverse();
                node.children.clear();
                for (const child of children) {
                    await child.unmount();
                }
                await mounted.unmount();
            },
        };
    }
    #assertUsable(node) {
        if (!this.#isUsable(node)) {
            throw new DomainError({
                code: "conflict",
                details: { appName: node.appName },
                message: `Stage ${node.appName} is no longer mounted`,
            });
        }
    }
    #isUsable(node) {
        if (!node.active) {
            return false;
        }
        // A node is intentionally allowed to compose while its loader is still
        // mounting off-DOM. Once committed, a detached host is stale and cannot
        // acquire more child sessions.
        return node.mounting || node.root.isConnected;
    }
    #requireEntry(appName) {
        const entry = this.#registry.entry(appName);
        if (entry === undefined) {
            throw new DomainError({
                code: "not_found",
                details: { appName },
                message: `Stage ${appName} is not installed or cached`,
            });
        }
        return entry;
    }
}
//# sourceMappingURL=composition-manager.js.map